import { getAccessToken } from './authSession';
import { redirectToSignIn } from './identity';
import {
  SessionExpiredError,
  canRefresh,
  ensureFreshAccessToken,
  refreshAccessToken,
} from './tokenRefresh';

function headerValue(headers, name) {
  if (!headers) return null;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    return headers.get(name);
  }
  const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : null;
}

function withBearer(headers, token) {
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const next = new Headers(headers);
    next.set('Authorization', `Bearer ${token}`);
    return next;
  }
  const next = { ...(headers || {}) };
  Object.keys(next).forEach((k) => {
    if (k.toLowerCase() === 'authorization') delete next[k];
  });
  next.Authorization = `Bearer ${token}`;
  return next;
}

function isBearer(value) {
  return typeof value === 'string' && /^bearer\s+\S+/i.test(value);
}

/**
 * `fetch` for Bearer-authenticated API calls.
 *
 * - Refreshes the access token first when it is about to expire.
 * - On 401, refreshes once (single-flight across callers) and retries the
 *   request with the new token.
 * - When no refresh is possible the stored session is cleared and the user is
 *   sent to /signin; the caller receives a SessionExpiredError.
 *
 * Requests that carry no Authorization header (demo X-User-Id/X-Org-Id mode,
 * public endpoints) pass straight through untouched.
 */
export async function authFetch(input, init = {}) {
  const sentAuth = headerValue(init.headers, 'Authorization');
  if (!isBearer(sentAuth)) {
    return fetch(input, init);
  }

  // Only manage tokens the app owns; a caller-supplied foreign token is left alone.
  const managed = sentAuth.slice('Bearer '.length).trim() === getAccessToken();
  let request = init;
  if (managed) {
    const fresh = await ensureFreshAccessToken().catch((err) => {
      if (err instanceof SessionExpiredError) {
        redirectToSignIn();
        throw err;
      }
      // Network blip while refreshing early: try the request with what we have.
      return getAccessToken();
    });
    if (fresh && fresh !== getAccessTokenFrom(sentAuth)) {
      request = { ...init, headers: withBearer(init.headers, fresh) };
    }
  }

  const response = await fetch(input, request);
  if (response.status !== 401 || !managed || !canRefresh()) {
    return response;
  }

  let token;
  try {
    await refreshAccessToken();
    token = getAccessToken();
  } catch (err) {
    redirectToSignIn();
    throw err instanceof SessionExpiredError ? err : new SessionExpiredError();
  }
  return fetch(input, { ...request, headers: withBearer(request.headers, token) });
}

function getAccessTokenFrom(authorizationHeader) {
  return authorizationHeader.slice('Bearer '.length).trim();
}
